import { Link, useLoaderData } from "react-router-dom";
import * as API from "../services/scl90";
import * as APIPACIENTE from "../services/pacientes";
import * as APIUSER from "../services/user";

import Scl90Questions from "./Scl90Questions";
import { calculateScl90Results } from "./Scl90Calc";
import { RadioOptions } from "./Scl90RadioOptions";

export async function loader({ params }) {
  const token = sessionStorage.getItem("token");

  // Si no hay token, simplemente NO llamamos a la API y retornamos null
  if (!token) {
    return null;
  }
  const scl90 = await API.getScl90ByID(params.scl90Id);
  // Verificar si `params.pacienteId` está disponible
  const pacienteMeta = params.pacienteId
    ? await APIPACIENTE.getPacienteByID(params.pacienteId)
    : await APIUSER.getCurrentUser();
  return { scl90, pacienteMeta };
}

export function Scl90Item(props) {
  const { scl90, pacienteMeta } = useLoaderData();
  const sexPaciente = pacienteMeta.meta?.sexo_paciente || "hombre";
  const errorMessage = scl90.message;

  // Verificar si `scl90.meta` existe antes de proceder
  if (!scl90.meta && !errorMessage) {
    return (
      <div id="content">
        <div>Cargando...</div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div id="content">
        <p>{errorMessage}</p>
      </div>
    );
  }

  const scl90Resp = scl90.meta?.scl90Resp;
  const metaFecha = scl90.meta?.metaFecha;
  const getResponse = (id) => scl90.meta?.[id] || "";

  // Componente Reutilizable para Preguntas de Radio
  const RadioQuestion = ({ id, label, name, value, disabled }) => {
    return (
      <div className="field">
        <div className="control grid--2col">
          <label htmlFor={id}>{label}</label>
          <div className="radio--5-col">
            {RadioOptions.map((option) => (
              <label
                className="radio"
                htmlFor={`${id}-${option.value}`}
                key={option.value}
              >
                <input
                  id={`${id}-${option.value}`}
                  name={name}
                  type="radio"
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => {}}
                  disabled={disabled}
                />
                {option.value}
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const scl90Date = new Date(scl90.date);
  const diffDate = Math.abs(new Date() - scl90Date) / 36e5;

  const {
    totalDimenSom,
    percSomHomb,
    percSomMuj,
    totalDimenObs,
    percObsHomb,
    percObsMuj,
    totalDimenSi,
    percSiHomb,
    percSiMuj,
    totalDimenDep,
    percDepHomb,
    percDepMuj,
    totalDimenAns,
    percAnsHomb,
    percAnsMuj,
    totalDimenHos,
    percHosHomb,
    percHosMuj,
    totalDimenFob,
    percFobHomb,
    percFobMuj,
    totalDimenPar,
    percParHomb,
    percParMuj,
    totalDimenPsic,
    percPsicHomb,
    percPsicMuj,
    totalDimenAdi,
    resultGsi,
    percGsiHomb,
    percGsiMuj,
    resultPst,
    percPstHomb,
    percPstMuj,
    resultPsdi,
    percPsdiHomb,
    percPsdiMuj,
  } = calculateScl90Results(scl90.meta);

  // resultados cuestionario cards

  const createResultProperties = (result, low, high) => {
    // Convertir los strings que vienen del meta a numbers
    result = parseFloat(result);
    low = parseFloat(low);
    high = parseFloat(high);
    const color =
      result >= low && result <= high
        ? "anali-result-good"
        : "anali-result-bad";
    const markIn = Math.min(
      Math.max(((result - low) / (high - low)) * 100, 0),
      100
    ).toFixed(0);
    const markOut =
      result < low
        ? "anali-result-low"
        : result > high
        ? "anali-result-high"
        : "";
    return { color, markIn, markOut };
  };

  const limitLow = 0;
  const limitHigh = 99;
  const limitHighDim = 4;

  const percSex = {
    percSom: sexPaciente === "hombre" ? percSomHomb : percSomMuj,
    percObs: sexPaciente === "hombre" ? percObsHomb : percObsMuj,
    percSi: sexPaciente === "hombre" ? percSiHomb : percSiMuj,
    percDep: sexPaciente === "hombre" ? percDepHomb : percDepMuj,
    percAns: sexPaciente === "hombre" ? percAnsHomb : percAnsMuj,
    percHos: sexPaciente === "hombre" ? percHosHomb : percHosMuj,
    percFob: sexPaciente === "hombre" ? percFobHomb : percFobMuj,
    percPar: sexPaciente === "hombre" ? percParHomb : percParMuj,
    percPsic: sexPaciente === "hombre" ? percPsicHomb : percPsicMuj,
    percGsi: sexPaciente === "hombre" ? percGsiHomb : percGsiMuj,
    percPst: sexPaciente === "hombre" ? percPstHomb : percPstMuj,
    percPsdi: sexPaciente === "hombre" ? percPsdiHomb : percPsdiMuj,
  };

  // Dimensiones y sus resultados
  const {
    color: dimSomColor,
    markIn: dimSomMarkIn,
    markOut: dimSomMarkOut,
  } = createResultProperties(percSex.percSom, limitLow, limitHigh);

  const {
    color: dimObsColor,
    markIn: dimObsMarkIn,
    markOut: dimObsMarkOut,
  } = createResultProperties(percSex.percObs, limitLow, limitHigh);

  const {
    color: dimSiColor,
    markIn: dimSiMarkIn,
    markOut: dimSiMarkOut,
  } = createResultProperties(percSex.percSi, limitLow, limitHigh);

  const {
    color: dimDepColor,
    markIn: dimDepMarkIn,
    markOut: dimDepMarkOut,
  } = createResultProperties(percSex.percDep, limitLow, limitHigh);

  const {
    color: dimAnsColor,
    markIn: dimAnsMarkIn,
    markOut: dimAnsMarkOut,
  } = createResultProperties(percSex.percAns, limitLow, limitHigh);

  const {
    color: dimHosColor,
    markIn: dimHosMarkIn,
    markOut: dimHosMarkOut,
  } = createResultProperties(percSex.percHos, limitLow, limitHigh);

  const {
    color: dimFobColor,
    markIn: dimFobMarkIn,
    markOut: dimFobMarkOut,
  } = createResultProperties(percSex.percFob, limitLow, limitHigh);

  const {
    color: dimParColor,
    markIn: dimParMarkIn,
    markOut: dimParMarkOut,
  } = createResultProperties(percSex.percPar, limitLow, limitHigh);

  const {
    color: dimPsicColor,
    markIn: dimPsicMarkIn,
    markOut: dimPsicMarkOut,
  } = createResultProperties(percSex.percPsic, limitLow, limitHigh);

  const {
    color: gsiColor,
    markIn: gsiMarkIn,
    markOut: gsiMarkOut,
  } = createResultProperties(percSex.percGsi, limitLow, limitHigh);

  const {
    color: pstColor,
    markIn: pstMarkIn,
    markOut: pstMarkOut,
  } = createResultProperties(percSex.percPst, limitLow, limitHigh);

  const {
    color: psdiColor,
    markIn: psdiMarkIn,
    markOut: psdiMarkOut,
  } = createResultProperties(percSex.percPsdi, limitLow, limitHigh);

  const {
    color: dimAdiColor,
    markIn: dimAdiMarkIn,
    markOut: dimAdiMarkOut,
  } = createResultProperties(totalDimenAdi, limitLow, limitHighDim);

  // Tooltip
  const TooltipComponent = ({ tooltipText }) => (
    <span className="tooltip" data-tooltip={tooltipText}>
      🛈
    </span>
  );

  return (
    <>
      <div id="content">
        {scl90.length === 0 ? (
          <div>Cargando...</div>
        ) : errorMessage ? (
          <p>{errorMessage}</p>
        ) : (
          <>
            <form id="formUpdatelement" className="newRadioButtons">
              <fieldset id="disabledContainer" disabled="disabled">
                <div className="grid--2col">
                  <div className="field">
                    <label htmlFor="scl90Resp">Responsable</label>
                    <div className="control">
                      <input
                        className="input"
                        id="scl90Resp"
                        required
                        type="text"
                        value={scl90Resp}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="metaFecha">Fecha visita</label>
                    <div className="control">
                      <input
                        className="input"
                        id="metaFecha"
                        required
                        type="date"
                        value={metaFecha}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <h3>Cuestionario SCL-90-R</h3>
                <p>
                  Ayúdanos a optimizar tu tratamiento y observa tu evolución.
                  Completa el cuestionario y OBTÉN 15€ A CAMBIO.
                </p>
                <br></br>
                <p>
                  A continuación, hay un listado de problemas que en ocasiones
                  tienen las personas. Por favor, léelos cuidadosamente y marca
                  el número que mejor describa HASTA QUÉ PUNTO TE HA AFECTADO
                  ESE PROBLEMA DURANTE LOS ÚLTIMOS 30 DÍAS. No olvides que debes
                  contestar todas las cuestiones planteadas.
                </p>
                <br></br>
                <div className="form-radio-container">
                  <div className="grid--2-col">
                    <div className="radio-capt radio--5-col">
                      <p>Nada</p>
                      <p>Un poco</p>
                      <p>Moderadamente</p>
                      <p>Bastante</p>
                      <p>Mucho</p>
                    </div>
                  </div>
                  {Scl90Questions.map((question) => (
                    <RadioQuestion
                      key={question.id}
                      id={question.id}
                      label={question.label}
                      name={question.id}
                      value={getResponse(question.id)}
                      disabled
                    />
                  ))}
                </div>
              </fieldset>
            </form>
            <form className="analitic-form">
              <h3>Resultado del cuestionario</h3>
              <div className="three-col-analitic">
                {/* Dimensión SOM */}
                <div className="anali-el">
                  <p className="anali-title">
                    Somatización
                    <TooltipComponent tooltipText="Somatización (SOM), refleja sintomatología psicosomática aunque también puede reflejar una patología médica subyacente." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimSomColor}`}>
                      {percSex.percSom}
                    </p>
                    <div className={`anali-bar-full ${dimSomMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimSomMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión OBS */}
                <div className="anali-el">
                  <p className="anali-title">
                    Obsesión compulsión
                    <TooltipComponent tooltipText="Obsesión compulsión (OBS), refleja conductas, pensamientos e impulsos absurdos e indeseados, que generan una angustia intensa y que son difíciles de resistir, evitar o eliminar, además de otras vivencias y fenómenos cognitivos característicos de los trastornos y personalidades obsesivas." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimObsColor}`}>
                      {percSex.percObs}
                    </p>
                    <div className={`anali-bar-full ${dimObsMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimObsMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión INT (antes SI) */}
                <div className="anali-el">
                  <p className="anali-title">
                    Sensibilidad interpersonal
                    <TooltipComponent tooltipText="Sensibilidad interpersonal (INT), refleja sentimientos de timidez y vergüenza, tendencia a sentirse inferior a los demás, hipersensibilidad a las opiniones y actitudes ajenas y, en general, incomodidad e inhibición en las relaciones interpersonales. " />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimSiColor}`}>
                      {percSex.percSi}
                    </p>
                    <div className={`anali-bar-full ${dimSiMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimSiMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión DEP */}
                <div className="anali-el">
                  <p className="anali-title">
                    Depresión
                    <TooltipComponent tooltipText="Depresión (DEP), refleja sentimientos de disforia, desánimo, anhedonia, desesperanza, impotencia y falta de energía, así como ideas autodestructivas y otras manifestaciones cognitivas y somáticas características de los estados depresivos." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimDepColor}`}>
                      {percSex.percDep}
                    </p>
                    <div className={`anali-bar-full ${dimDepMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimDepMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión ANS */}
                <div className="anali-el">
                  <p className="anali-title">
                    Ansiedad
                    <TooltipComponent tooltipText="Ansiedad (ANS), refleja manifestaciones clínicas de la ansiedad, tanto generalizada como aguda (pánico), así como tensión emocional y sus manifestaciones psicosomáticas." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimAnsColor}`}>
                      {percSex.percAns}
                    </p>
                    <div className={`anali-bar-full ${dimAnsMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimAnsMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión HOS */}
                <div className="anali-el">
                  <p className="anali-title">
                    Hostilidad
                    <TooltipComponent tooltipText="Hostilidad (HOS), refleja pensamientos, sentimientos y conductas propios de estados de agresividad, ira, irritabilidad, rabia y resentimiento. " />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimHosColor}`}>
                      {percSex.percHos}
                    </p>
                    <div className={`anali-bar-full ${dimHosMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimHosMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión FOB */}
                <div className="anali-el">
                  <p className="anali-title">
                    Ansiedad fóbica
                    <TooltipComponent tooltipText="Ansiedad fóbica (FOB), refleja el miedo persistente, irracional y desproporcionado a un animal o persona, lugar, objeto o situación, generalmente complicado por conductas evitativas o de huida." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimFobColor}`}>
                      {percSex.percFob}
                    </p>
                    <div className={`anali-bar-full ${dimFobMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimFobMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión PAR */}
                <div className="anali-el">
                  <p className="anali-title">
                    Ideación paranoide
                    <TooltipComponent tooltipText="Ideación paranoide (PAR), refleja distintos aspectos de la conducta paranoide, como suspicacia, centralismo autorreferencial e ideación delirante, hostilidad, grandiosidad, miedo a la pérdida de autonomía y necesidad de control." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimParColor}`}>
                      {percSex.percPar}
                    </p>
                    <div className={`anali-bar-full ${dimParMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimParMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión PSI */}
                <div className="anali-el">
                  <p className="anali-title">
                    Psicoticismo
                    <TooltipComponent tooltipText="Psicoticismo (PSI), refleja sentimientos de alienación social." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimPsicColor}`}>
                      {percSex.percPsic}
                    </p>
                    <div className={`anali-bar-full ${dimPsicMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimPsicMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Resultado del Indice Global de Severidad */}
                <div className="anali-el">
                  <p className="anali-title">
                    Indice Global de Gravedad
                    <TooltipComponent tooltipText="Indice Global de Gravedad (GSI), refleja la intensidad del sufrimiento psíquico y psicosomático global." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${gsiColor}`}>
                      {percSex.percGsi}
                    </p>
                    <div className={`anali-bar-full ${gsiMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${gsiMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Resultado de PST [Falta valor]*/}
                <div className="anali-el">
                  <p className="anali-title">
                    Total de Síntomas Positivos
                    <TooltipComponent tooltipText="Total de Síntomas Positivos (PST), refleja el número total de síntomas presentes." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${pstColor}`}>
                      {percSex.percPst}
                    </p>
                    <div className={`anali-bar-full ${pstMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${pstMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Resultado de PSDI */}
                <div className="anali-el">
                  <p className="anali-title">
                    Distrés de Síntomas Positivos
                    <TooltipComponent tooltipText="Distrés de Síntomas Positivos (PSDI), refleja la intensidad de los síntomas." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${psdiColor}`}>
                      {percSex.percPsdi}
                    </p>
                    <div className={`anali-bar-full ${psdiMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${psdiMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHigh}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dimensión ADI */}
                <div className="anali-el">
                  <p className="anali-title">
                    Síntomas misceláneos
                    <TooltipComponent tooltipText="Síntomas misceláneos (ADI), refleja la gravedad de la depresión." />
                  </p>
                  <div className="anali-result-container">
                    <p className={`anali-result ${dimAdiColor}`}>
                      {totalDimenAdi}
                    </p>
                    <div className={`anali-bar-full ${dimAdiMarkOut}`}>
                      <div className="anali-bar anali-bar-low"></div>
                      <div className="anali-bar anali-bar-main">
                        <div
                          className="anali-bar-mark"
                          style={{
                            left: `${dimAdiMarkIn}%`,
                          }}
                        ></div>
                      </div>
                      <div className="anali-bar anali-bar-high"></div>
                    </div>
                    <div className="anali-limit-container">
                      <p className="anali-limit anali-limit-low">{limitLow}</p>
                      <p className="anali-limit anali-limit-high">
                        {limitHighDim}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
      {!errorMessage && (
        <div id="actions">
          {(diffDate <= 72 || props.userRole.includes("administrador")) && (
            <Link className="button is-link" to={`edit`}>
              Editar
            </Link>
          )}
        </div>
      )}
    </>
  );
}
